import { create } from 'zustand';
import i18n from '@/config/i18n';
import {
  setTokens,
  updateAccessToken,
  getAccessToken,
  clearTokens,
  isTokenExpired,
  hasStoredAccessToken,
  initializeTokenCache,
} from '@/services/authTokens';
import { authApi, tenantApi, setCachedToken, clearCachedToken } from '@/services/api';
import { queryClient } from '@/config/queryClient';
import type { TenantConfig } from '@shared/types/tenant-config';

export interface AuthenticatedUser {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string | null; // US phone number (formatted: (555) 555-5555)
  role: 'support_staff' | 'teacher' | 'admin' | 'super_admin' | 'parent';
  tenantId: string;
  emailVerified?: boolean;
}

interface TenantInfo {
  id: string;
  subdomain: string;
  schoolName: string;
  timezone: string; // School timezone (e.g., 'America/Denver')
  settings: TenantConfig; // Nested structure
  allowedModules: {
    attendance: boolean;
    parentPortal: boolean;
    sms: boolean;
    messaging: boolean;
    schoolwideSms: boolean;
  };
}

interface AuthStore {
  user: AuthenticatedUser | null;
  tenant: TenantInfo | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  viewMode: 'admin' | 'teacher' | 'support_staff'; // Only relevant for admin role
  setTenant: (tenant: TenantInfo | null) => void;
  setViewMode: (mode: 'admin' | 'teacher' | 'support_staff') => void;
  initializeAuth: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

/**
 * Validate and cast role string to proper type
 */
const validateRole = (role: string): AuthenticatedUser['role'] => {
  const validRoles: AuthenticatedUser['role'][] = ['support_staff', 'teacher', 'admin', 'super_admin', 'parent'];
  if (validRoles.includes(role as AuthenticatedUser['role'])) {
    return role as AuthenticatedUser['role'];
  }
  throw new Error(`Invalid role: ${role}`);
};

// Token refresh interval (check every 60 seconds)
let tokenRefreshInterval: ReturnType<typeof setInterval> | null = null;

/**
 * Start automatic token refresh
 * Refreshes access token when it's about to expire
 */
function startTokenRefresh(store: AuthStore) {
  // Clear any existing interval
  if (tokenRefreshInterval) {
    clearInterval(tokenRefreshInterval);
  }

  // Check every 60 seconds if token needs refresh
  tokenRefreshInterval = setInterval(async () => {
    // Skip if not authenticated
    if (!useAuthStore.getState().isAuthenticated) {
      return;
    }

    // Check if token is expired or about to expire (within 2 minutes)
    if (isTokenExpired(2 * 60 * 1000)) {
      try {
        console.log('[Auth] Refreshing access token...');
        // No refreshToken parameter - cookie is sent automatically
        const result = await authApi.refresh();
        updateAccessToken(result.accessToken, result.expiresIn);
        setCachedToken(result.accessToken, Date.now() + result.expiresIn * 1000);
        console.log('[Auth] Token refreshed successfully');
      } catch (error) {
        console.error('[Auth] Token refresh failed:', error);
        // If refresh fails, log out the user
        await store.logout();
      }
    }
  }, 60 * 1000);
}

/**
 * Stop automatic token refresh
 */
function stopTokenRefresh() {
  if (tokenRefreshInterval) {
    clearInterval(tokenRefreshInterval);
    tokenRefreshInterval = null;
  }
}

export const useAuthStore = create<AuthStore>((set, get) => ({
  user: null,
  tenant: null,
  isLoading: true,
  isAuthenticated: false,
  error: null,
  viewMode: (localStorage.getItem('checkademic_view_mode') as 'admin' | 'teacher' | 'support_staff') || 'admin',

  setTenant: (tenant) => {
    set({ tenant });

    // Cache in localStorage with timestamp
    if (tenant) {
      localStorage.setItem('tenant_config', JSON.stringify(tenant));
      localStorage.setItem('tenant_config_time', Date.now().toString());
    } else {
      localStorage.removeItem('tenant_config');
      localStorage.removeItem('tenant_config_time');
    }
  },

  setViewMode: (mode) => {
    const currentMode = get().viewMode;
    if (currentMode !== mode) {
      // Clear cache to prevent stale data when switching views
      queryClient.clear();
    }
    localStorage.setItem('checkademic_view_mode', mode);
    set({ viewMode: mode });
  },

  initializeAuth: async () => {
    try {
      // Initialize token cache from localStorage
      initializeTokenCache();

      // Check if we have stored access token
      if (hasStoredAccessToken()) {
        // Get access token (returns null if expired)
        let accessToken = getAccessToken();

        // If access token expired, try to refresh using httpOnly cookie
        if (!accessToken) {
          try {
            console.log('[Auth] Access token expired, refreshing...');
            // No refreshToken parameter - cookie is sent automatically
            const result = await authApi.refresh();
            updateAccessToken(result.accessToken, result.expiresIn);
            accessToken = result.accessToken;
          } catch {
            console.warn('[Auth] Refresh token invalid, clearing session');
            clearTokens();
            clearCachedToken();
          }
        }

        if (accessToken) {
          // Cache the token in memory for API calls
          const expiresAt = parseInt(localStorage.getItem('token_expires_at') || '0', 10);
          setCachedToken(accessToken, expiresAt);

          // Fetch user info from our backend
          try {
            const response = await authApi.getMe();
            set({
              user: {
                ...response.user,
                role: validateRole(response.user.role),
              },
              isAuthenticated: true,
              isLoading: false,
              error: null,
            });

            // Update tenant with caching
            get().setTenant(response.tenant);

            // Reset language to English for non-parent roles (handles stale localStorage from parent sessions)
            if (response.user.role !== 'parent' && i18n.language !== 'en') {
              i18n.changeLanguage('en');
            }

            // Start automatic token refresh
            startTokenRefresh(get());
          } catch (error: unknown) {
            console.error('Failed to fetch user info:', error);

            // If 401, clear session (likely invalid JWT)
            const errorStatus = (error as { status?: number })?.status;
            if (errorStatus === 401) {
              clearTokens();
              clearCachedToken();
              localStorage.removeItem('tenant_config');
              localStorage.removeItem('tenant_config_time');
            }

            set({
              user: null,
              isAuthenticated: false,
              isLoading: false,
              error: null,
            });
            get().setTenant(null);
          }
        } else {
          // No valid token - fetch public tenant config for unauthenticated access
          await loadPublicTenantConfig(get);
          set({ user: null, isAuthenticated: false, isLoading: false });
          clearCachedToken();
        }
      } else {
        // No stored tokens - fetch public tenant config for unauthenticated access
        await loadPublicTenantConfig(get);
        set({ user: null, isAuthenticated: false, isLoading: false });
        clearCachedToken();
      }
    } catch (error) {
      console.error('Error initializing auth:', error);
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to initialize authentication',
      });
      get().setTenant(null);
    }
  },

  login: async (email: string, password: string) => {
    try {
      set({ isLoading: true, error: null });

      // Call custom login endpoint
      const result = await authApi.login(email, password);

      // Store access token (refresh token is in httpOnly cookie set by backend)
      setTokens(result.accessToken, result.expiresIn);

      // Cache the token in memory for API calls
      const expiresAt = Date.now() + result.expiresIn * 1000;
      setCachedToken(result.accessToken, expiresAt);

      // Fetch full user info (includes tenant)
      const meResponse = await authApi.getMe();

      set({
        user: {
          ...meResponse.user,
          role: validateRole(meResponse.user.role),
        },
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });

      // Update tenant with caching
      get().setTenant(meResponse.tenant);

      // Reset language to English for non-parent roles (handles stale localStorage from parent sessions)
      if (meResponse.user.role !== 'parent' && i18n.language !== 'en') {
        i18n.changeLanguage('en');
      }

      // Start automatic token refresh
      startTokenRefresh(get());
    } catch (error) {
      console.error('Login failed:', error);
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Login failed',
      });
      throw error;
    }
  },

  logout: async () => {
    try {
      // Stop token refresh
      stopTokenRefresh();

      // Call backend logout (invalidates session server-side, reads refresh token from cookie)
      try {
        await authApi.logout();
      } catch (error) {
        console.warn('Backend logout failed, continuing with client logout:', error);
      }

      // Clear all tokens and preferences
      clearTokens();
      clearCachedToken();
      localStorage.removeItem('tenant_config');
      localStorage.removeItem('tenant_config_time');
      localStorage.removeItem('checkademic_view_mode');

      // Reset language to English so next user doesn't inherit Spanish from a parent session
      i18n.changeLanguage('en');

      // Clear React Query cache to prevent stale data when switching users
      queryClient.clear();

      set({
        user: null,
        isAuthenticated: false,
        error: null,
      });

      // Reload public tenant config so login page still shows school branding
      await loadPublicTenantConfig(get);
    } catch (error) {
      console.error('Error during logout:', error);
      set({
        error: error instanceof Error ? error.message : 'Logout failed',
      });
      throw error;
    }
  },
}));

/**
 * Helper to load public tenant config for unauthenticated users
 */
async function loadPublicTenantConfig(get: () => AuthStore) {
  try {
    const publicConfig = await tenantApi.getPublicConfig();
    if (publicConfig) {
      get().setTenant(publicConfig.tenant as TenantInfo);
    } else {
      get().setTenant(null);
    }
  } catch (error) {
    console.error('Failed to fetch public tenant config:', error);
    get().setTenant(null);
  }
}
